import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { createDepartment, deleteDepartment, getDepartments, updateDepartment } from '../../../services/departmentService';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';

interface Department {
  id: number;
  name: string;
  code: string;
  description?: string | null;
  created_at?: string;
}

const emptyForm = { name: '', code: '', description: '' };

const Departments: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadDepartments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getDepartments();
      setDepartments(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load departments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const name = form.name.trim();
    const code = form.code.trim().toUpperCase();

    if (!name || !code) {
      setError('Department name and code are required.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      const payload = { name, code, description: form.description.trim() };
      if (editingId !== null) {
        await updateDepartment(editingId, payload);
      } else {
        await createDepartment(payload);
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditingId(null);
      setSuccess(editingId !== null ? 'Department updated successfully.' : 'Department added successfully.');
      await loadDepartments();
    } catch (err: any) {
      const validationErrors = err?.response?.data?.errors;
      const firstValidationError = validationErrors && Object.values(validationErrors).flat()[0];
      setError(String(firstValidationError || err?.response?.data?.message || 'Failed to add department.'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (department: Department) => {
    setEditingId(department.id);
    setForm({
      name: department.name || '',
      code: department.code || '',
      description: department.description || '',
    });
    setShowForm(true);
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingId(deleteTarget.id);
      setError('');
      setSuccess('');
      await deleteDepartment(deleteTarget.id);
      if (editingId === deleteTarget.id) cancelForm();
      setDeleteTarget(null);
      setSuccess('Department deleted successfully.');
      await loadDepartments();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete department.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white min-h-full p-8 overflow-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Departments</h2>
          <p className="text-sm text-gray-500 mt-1">Manage departments available throughout the system.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (showForm) {
              cancelForm();
            } else {
              setShowForm(true);
              setEditingId(null);
              setForm(emptyForm);
            }
            setError('');
            setSuccess('');
          }}
          className="flex items-center gap-2 rounded-md bg-[#e84545] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />}
          {showForm ? 'Cancel' : 'Add Department'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Department Name <span className="text-red-500">*</span>
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-normal outline-none focus:border-blue-500"
                placeholder="Operations"
                maxLength={100}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Code <span className="text-red-500">*</span>
              <input
                value={form.code}
                onChange={(event) => setForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-normal uppercase outline-none focus:border-blue-500"
                placeholder="OPS"
                maxLength={20}
              />
            </label>
          </div>
          <label className="mt-4 block text-sm font-medium text-gray-700">
            Description
            <textarea
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              className="mt-1 min-h-[90px] w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 font-normal outline-none focus:border-blue-500"
              placeholder="Responsible for plant operations and shift activities."
              maxLength={500}
            />
          </label>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-[#143d7d] px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (editingId !== null ? 'Saving...' : 'Adding...') : editingId !== null ? 'Save Changes' : 'Add Department'}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full border-collapse text-left">
          <thead className="bg-gray-50 text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  Loading departments...
                </td>
              </tr>
            ) : departments.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  No departments found.
                </td>
              </tr>
            ) : (
              departments.map((department) => (
                <tr key={department.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{department.name}</td>
                  <td className="px-4 py-3 text-gray-700">{department.code}</td>
                  <td className="px-4 py-3 text-gray-600">{department.description || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(department)}
                        className="rounded p-2 text-blue-700 hover:bg-blue-50"
                        aria-label={`Edit ${department.name}`}
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(department)}
                        disabled={deletingId === department.id}
                        className="rounded p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Delete ${department.name}`}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Department?"
        message={`Are you sure you want to delete “${deleteTarget?.name || ''}”?`}
        confirmText="Yes, Delete"
        loadingText="Deleting..."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={deletingId !== null}
      />
      <SuccessModal
        isOpen={!!success}
        onClose={() => setSuccess('')}
        title="Success"
        message={success}
        buttonText="OK"
        onButtonClick={() => setSuccess('')}
      />
      <ErrorModal isOpen={!!error} onClose={() => setError('')} title="Operation Failed" message={error} buttonText="OK" />
    </div>
  );
};

export default Departments;
